import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { Truck, UserCheck, Clock } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Custom ambulance icon
const ambulanceIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/2967/2967350.png',
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});

const ambulances = [
  {
    id: 'AMB-001',
    position: [12.9716, 77.5946],
    status: 'En Route',
    driver: 'John Smith',
    patient: 'John Doe',
    destination: 'City General Hospital',
    eta: '5 min',
  },
  {
    id: 'AMB-002',
    position: [12.9750, 77.6050],
    status: 'On Scene',
    driver: 'Sarah Johnson',
    patient: 'Jane Smith',
    destination: 'Downtown Medical Center',
    eta: 'Arrived',
  },
  {
    id: 'AMB-003',
    position: [12.9650, 77.5900],
    status: 'Available',
    driver: 'Mike Wilson',
    patient: null,
    destination: null,
    eta: null,
  },
];

const getLocalDateString = () => {
  const now = new Date();
  return now.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getLocalTimeZone = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const mins = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${tz} (GMT${sign}${hours}:${mins})`;
};

const LiveTracking = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="p-8 bg-neutral-50 min-h-screen">
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2 tracking-tight">Live Ambulance Tracking</h1>
        <p className="text-neutral-500">
          Real-time location of ambulances and their current status
        </p>
        <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-400">
          <Clock className="w-4 h-4" />
          <span>{currentTime.toLocaleTimeString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalDateString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalTimeZone()}</span>
        </div>
      </div>
      <div className="card">
        <div className="map-container">
          <MapContainer center={[12.9716, 77.5946]} zoom={13} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {ambulances.map((amb) => (
              <Marker key={amb.id} position={amb.position} icon={ambulanceIcon}>
                <Popup>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <Truck className="w-4 h-4 text-primary-600" />
                      <span className="font-bold text-neutral-900">{amb.id}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <UserCheck className="w-4 h-4 text-neutral-400" />
                      <span>Driver: {amb.driver}</span>
                    </div>
                    {amb.patient && (
                      <div className="text-sm text-neutral-700">Patient: {amb.patient}</div>
                    )}
                    {amb.destination && (
                      <div className="text-sm text-neutral-700">Destination: {amb.destination}</div>
                    )}
                    <div className="text-xs mt-1">
                      <span className="status-busy">{amb.status}</span>
                      {amb.eta && (
                        <span className="ml-2 text-neutral-400">ETA: {amb.eta}</span>
                      )}
                    </div>
                  </div>
                </Popup>
                <Circle
                  center={amb.position}
                  radius={300}
                  pathOptions={{ color: amb.status === 'Available' ? '#10b981' : '#14b8a6', fillOpacity: 0.1 }}
                />
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default LiveTracking; 